import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { GraduationCap, X, Info } from 'lucide-react';
import Card from '../../../../ui/canon/Card';
import SearchBar from '../../../../ui/canon/SearchBar';
import { listOfStates, listOfProvinces, provinceFrNames } from '../../lib/findBobUtils';
import StudentCandidateCard from './StudentCandidateCard';
import StudentProfileModal from './StudentProfileModal';

const StudentCandidatesBrowser = ({
  students,
  activePosts,
  messagingWithStudentIds,
  currentUser,
  onFilterChange,
  filters,
  companyCountry,
}) => {
  const intl = useIntl();
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [localFilters, setLocalFilters] = useState(filters || {});
  const [showSmartMatchBanner, setShowSmartMatchBanner] = useState(true);

  // Local search filtering (on top of server-side filters)
  const filteredStudents = useMemo(() => {
    return students.filter(student => {
      if (!searchTerm) return true;
      const searchLower = searchTerm.toLowerCase();
      const displayName = student.user?.allowed_name || student.user?.name || '';
      const school = student.school || '';
      const degree = student.degree_program || '';
      return (
        displayName.toLowerCase().includes(searchLower) ||
        school.toLowerCase().includes(searchLower) ||
        degree.toLowerCase().includes(searchLower)
      );
    });
  }, [students, searchTerm]);

  // Smart match: find students matching active posts
  const smartMatches = useMemo(() => {
    if (!activePosts || activePosts.length === 0) return null;

    const activePost = activePosts.find(p => p.status === 'published');
    if (!activePost) return null;

    const matches = students.filter(s => {
      const typeMatch =
        (activePost.position_type === 'internship' && s.employment_type === 'internship') ||
        (activePost.position_type === 'full-time' && s.employment_type === 'full-time');
      const locationMatch = s.state && activePost.location?.toLowerCase().includes(s.state.toLowerCase());
      return typeMatch && locationMatch;
    });

    return matches.length > 0 ? { count: matches.length, post: activePost } : null;
  }, [activePosts, students]);

  const isCanadian = companyCountry && companyCountry.toLowerCase().match(/^(ca|can|canada)$/);
  const stateOptions = isCanadian ? listOfProvinces : listOfStates;
  const stateLabel = isCanadian
    ? intl.formatMessage({ id: 'hiring.browser.filter.all_provinces' })
    : intl.formatMessage({ id: 'hiring.browser.filter.all_states' });

  const handleFilterChange = (key, value) => {
    const newFilters = { ...localFilters, [key]: value || null };
    setLocalFilters(newFilters);
    onFilterChange(newFilters);
  };

  const clearFilters = () => {
    const emptyFilters = {
      school: null,
      degree_program: null,
      graduation_year: null,
      employment_type: null,
      candidate_track: null,
      state: null,
    };
    setLocalFilters(emptyFilters);
    setSearchTerm('');
    onFilterChange(emptyFilters);
  };

  const handleStudentClick = (student) => {
    setSelectedStudent(student);
    setIsModalOpen(true);
  };

  const hasActiveFilters = Object.values(localFilters).some(v => v) || searchTerm;

  return (
    <div className="space-y-6">
      {/* Smart Match Banner */}
      {smartMatches && showSmartMatchBanner && (
        <Card padding="md">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center flex-shrink-0">
                <Info className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1">
                <p className="font-medium text-blue-900 mb-1">
                  {intl.formatMessage(
                    { id: 'hiring.browser.smart_match.heading' },
                    { count: smartMatches.count, title: smartMatches.post.title }
                  )}
                </p>
                <p className="text-sm text-blue-700">
                  {smartMatches.post.position_type === 'internship'
                    ? intl.formatMessage(
                        { id: 'hiring.browser.smart_match.seeking_internship' },
                        { location: smartMatches.post.location?.split(',')[0] }
                      )
                    : intl.formatMessage(
                        { id: 'hiring.browser.smart_match.seeking_full_time' },
                        { location: smartMatches.post.location?.split(',')[0] }
                      )}
                </p>
              </div>
              <button
                onClick={() => setShowSmartMatchBanner(false)}
                className="flex-shrink-0 p-1 rounded text-blue-400 hover:text-blue-600"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </Card>
      )}

      {/* Filters */}
      <Card padding="lg">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Search - full width */}
          <div className="lg:col-span-3">
            <SearchBar
              value={searchTerm}
              onChange={setSearchTerm}
              placeholder={intl.formatMessage({ id: 'hiring.browser.search_placeholder' })}
              variant="primary"
              size="lg"
            />
          </div>

          {/* School */}
          <input
            type="text"
            placeholder={intl.formatMessage({ id: 'hiring.browser.filter.school' })}
            value={localFilters.school || ''}
            onChange={(e) => handleFilterChange('school', e.target.value)}
            className="px-3 py-2 rounded-md border border-succession-border bg-white text-succession-text-primary focus:outline-none focus:ring-2 focus:ring-succession-primary"
          />

          {/* Degree Program */}
          <input
            type="text"
            placeholder={intl.formatMessage({ id: 'hiring.browser.filter.degree_program' })}
            value={localFilters.degree_program || ''}
            onChange={(e) => handleFilterChange('degree_program', e.target.value)}
            className="px-3 py-2 rounded-md border border-succession-border bg-white text-succession-text-primary focus:outline-none focus:ring-2 focus:ring-succession-primary"
          />

          {/* Employment Type */}
          <select
            value={localFilters.employment_type || ''}
            onChange={(e) => handleFilterChange('employment_type', e.target.value)}
            className="px-3 py-2 rounded-md border border-succession-border bg-white text-succession-text-primary focus:outline-none focus:ring-2 focus:ring-succession-primary"
          >
            <option value="">{intl.formatMessage({ id: 'hiring.browser.filter.all_types' })}</option>
            <option value="internship">{intl.formatMessage({ id: 'hiring.browser.filter.internship' })}</option>
            <option value="full-time">{intl.formatMessage({ id: 'hiring.browser.filter.full_time' })}</option>
          </select>

          {/* Candidate Type */}
          <select
            value={localFilters.candidate_track || ''}
            onChange={(e) => handleFilterChange('candidate_track', e.target.value)}
            className="px-3 py-2 rounded-md border border-succession-border bg-white text-succession-text-primary focus:outline-none focus:ring-2 focus:ring-succession-primary"
          >
            <option value="">All Candidate Types</option>
            <option value="student">Student</option>
            <option value="employee">Employee</option>
          </select>

          {/* State / Province */}
          <select
            value={localFilters.state || ''}
            onChange={(e) => handleFilterChange('state', e.target.value)}
            className="px-3 py-2 rounded-md border border-succession-border bg-white text-succession-text-primary focus:outline-none focus:ring-2 focus:ring-succession-primary"
          >
            <option value="">{stateLabel}</option>
            {stateOptions.map((s) => (
              <option key={s} value={s}>
                {isCanadian && intl.locale === 'fr' ? (provinceFrNames[s] || s) : s}
              </option>
            ))}
          </select>

          {/* Graduation Year */}
          <select
            value={localFilters.graduation_year || ''}
            onChange={(e) => handleFilterChange('graduation_year', e.target.value ? parseInt(e.target.value, 10) : null)}
            className="px-3 py-2 rounded-md border border-succession-border bg-white text-succession-text-primary focus:outline-none focus:ring-2 focus:ring-succession-primary"
          >
            <option value="">{intl.formatMessage({ id: 'hiring.browser.filter.all_years' })}</option>
            <option value="2025">2025</option>
            <option value="2026">2026</option>
            <option value="2027">2027</option>
            <option value="2028">2028</option>
            <option value="2029">2029</option>
          </select>
        </div>

        {/* Clear Filters / Count */}
        {hasActiveFilters && (
          <div className="flex items-center justify-between mt-4 pt-4 border-t border-succession-border">
            <p className="text-sm text-succession-text-secondary">
              {intl.formatMessage({ id: 'hiring.browser.candidates_found' }, { count: filteredStudents.length })}
            </p>
            <button
              onClick={clearFilters}
              className="text-sm text-succession-primary hover:text-succession-primary-dark font-medium"
            >
              {intl.formatMessage({ id: 'hiring.browser.clear_all' })}
            </button>
          </div>
        )}
      </Card>

      {/* Results Count (when no filters) */}
      {!hasActiveFilters && (
        <p className="text-sm text-succession-text-secondary">
          {intl.formatMessage({ id: 'hiring.browser.candidates_found' }, { count: filteredStudents.length })}
        </p>
      )}

      {/* Student Cards Grid */}
      {filteredStudents.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredStudents.map((student) => (
            <StudentCandidateCard
              key={student.id}
              student={student}
              onClick={handleStudentClick}
              isMessaging={messagingWithStudentIds?.has(student.user?.id)}
            />
          ))}
        </div>
      ) : (
        <Card padding="lg">
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <GraduationCap className="w-8 h-8 text-succession-text-muted" />
            </div>
            <h3 className="text-lg font-semibold text-succession-text-primary mb-2">
              {hasActiveFilters
                ? intl.formatMessage({ id: 'hiring.browser.empty.filters_heading' })
                : intl.formatMessage({ id: 'hiring.browser.empty.no_candidates_heading' })}
            </h3>
            <p className="text-succession-text-secondary mb-4">
              {hasActiveFilters
                ? intl.formatMessage({ id: 'hiring.browser.empty.filters_body' })
                : intl.formatMessage({ id: 'hiring.browser.empty.no_candidates_body' })}
            </p>
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="px-4 py-2 bg-succession-primary text-white rounded-md hover:bg-succession-primary-dark transition-colors"
              >
                {intl.formatMessage({ id: 'hiring.browser.clear_filters' })}
              </button>
            )}
          </div>
        </Card>
      )}

      {/* Student Profile Modal */}
      <StudentProfileModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        student={selectedStudent}
        isMessaging={selectedStudent && messagingWithStudentIds?.has(selectedStudent.user?.id)}
      />
    </div>
  );
};

StudentCandidatesBrowser.propTypes = {
  students: PropTypes.array.isRequired,
  activePosts: PropTypes.array,
  messagingWithStudentIds: PropTypes.instanceOf(Set),
  currentUser: PropTypes.object,
  onFilterChange: PropTypes.func.isRequired,
  filters: PropTypes.object,
  companyCountry: PropTypes.string,
};

export default StudentCandidatesBrowser;
